import React from 'react'
import axios from 'axios'

/**
 * Types and Interface
 */
import {
  DefaultComponentProps,
}
from '@interface/common'

/**
 * Helpers
 */
import {pure} from '@react/store'
import {validate} from '@helpers/common'

/**
 * App configuration
 */
import config from '@config/common'

/**
 * Images
 */
import Spinner from '@assets/images/spinner.gif'


const REASONS = [
  {value: '',             label: 'All'},
  {value: 'bullying',     label: 'Bullying'},
  {value: 'harassment',   label: 'Harassment'},
  {value: 'hate_speech',  label: 'Hate Speech'},
  {value: 'spam',         label: 'Spam'},
  {value: 'misinformation', label: 'Misinformation'},
  {value: 'other',        label: 'Other'},
]


/**
 * Reports Screen
 */
export default pure(({user, store}: DefaultComponentProps) => {
  const state = store.useLocalState({
    email: '',
    reason: '',
    fetching: false,
    error: null as string | null,
    generated: false,
  })

  /**
   * Decode error body from arraybuffer responses
   */
  function errorMessage(e: any): string {
    const data = e?.response?.data
    if (!data) {
      return 'Failed to generate report. Please try again.'
    }
    if (typeof data === 'string') {
      return data
    }
    try {
      const text = new TextDecoder().decode(data)
      const json = JSON.parse(text)
      return json?.result?.error || json?.message || 'Failed to generate report. Please try again.'
    }
    catch {
      return 'Failed to generate report. Please try again.'
    }
  }

  /**
   * Download the report PDF
   */
  async function generate() {
    const email = state.email.trim().toLowerCase()

    if (email && !validate.email(email)) {
      state.set({error: 'Enter a valid email address.', generated: false})
      return
    }

    state.set({fetching: true, error: null, generated: false})

    try {
      const params: Record<string, string> = {}
      if (email) params.email = email
      if (state.reason) params.reason = state.reason

      const headers: Record<string, string> = {}
      // Prefer cookie auth; only send Bearer when a real token exists
      if (user?.token) {
        headers.Authorization = `Bearer ${user.token}`
      }

      const response = await axios.get(`${config.env.ENDPOINT}/reports`, {
        headers,
        params,
        withCredentials: true,
        responseType: 'arraybuffer',
      })

      const blob = new Blob([response.data], {type: 'application/pdf'})
      const objectUrl = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = email ? `report-${email}.pdf` : 'report.pdf'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(objectUrl)

      state.set({fetching: false, generated: true})
    }
    catch (e: any) {
      const status = e?.response?.status
      const message =
        status === 401
          ? 'Session expired. Please log in again.'
          : errorMessage(e)
      state.set({fetching: false, error: message})
    }
  }

  return (
    <div className="inner">
      <header>
        <div className="left">
          <h3 className="margin-0">Safety Report</h3>
        </div>
      </header>

      <div style={{maxWidth: 560, marginTop: 30}}>
        <p style={{color: '#555', marginBottom: 24, lineHeight: 1.6}}>
          Generate a PDF report of all reported posts and bullying alerts.
          Filter by a specific user email and/or alert reason, or leave blank to
          include all records.
        </p>

        <div style={{marginBottom: 20}}>
          <label style={{display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13}}>
            User Email <span style={{fontWeight: 400, color: '#999'}}>(optional)</span>
          </label>
          <input
            type="email"
            placeholder="e.g. student@school.edu"
            value={state.email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              state.set({email: e.target.value})
            }
            style={{
              width: '100%',
              padding: '9px 12px',
              border: '1px solid #ddd',
              borderRadius: 6,
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{marginBottom: 28}}>
          <label style={{display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13}}>
            Alert Reason <span style={{fontWeight: 400, color: '#999'}}>(optional)</span>
          </label>
          <select
            value={state.reason}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              state.set({reason: e.target.value})
            }
            style={{
              width: '100%',
              padding: '9px 12px',
              border: '1px solid #ddd',
              borderRadius: 6,
              fontSize: 13,
              background: '#fff',
              boxSizing: 'border-box',
            }}
          >
            {REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
        </div>

        <span
          onClick={() => !state.fetching && generate()}
          className="button-3"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            cursor: state.fetching ? 'not-allowed' : 'pointer',
            opacity: state.fetching ? 0.7 : 1,
          }}
        >
          {state.fetching ? (
            <>
              <img alt="Loading" src={Spinner} style={{width: 16, height: 16}}/>
              Generating…
            </>
          ) : (
            'Generate PDF'
          )}
        </span>

        {state.generated && !state.error && (
          <p style={{marginTop: 18, color: '#2a9d50', fontSize: 13}}>
            Report downloaded successfully.
          </p>
        )}

        {state.error && (
          <p style={{marginTop: 18, color: '#c0392b', fontSize: 13}}>
            {state.error}
          </p>
        )}
      </div>
    </div>
  )
})
